import type { NodeData } from '@antv/g6'
import { ExtensionCategory, Graph, register, treeToGraphData } from '@antv/g6'
import type { NodeStyle } from '@antv/g6/lib/spec/element/node'
import { ReactNode } from '@antv/g6-extension-react'
import { useCallback, useEffect, useRef } from 'react'
import data from '@/api/tree-data.json'
import type { LineageNodeData } from '@/types/lineage'
import { CollapseExpandTree } from './behavior/collapse-expand-tree'
import { HoverElement } from './behavior/hover-element'
import { ACTIVE_COLOR } from './constant'
import { CustomCubic } from './edge/custom-cubic'
import { LineageNode } from './node/lineage-node'
import { IndentedNode } from './plugin/indented-node'
import { RemoteDataSource } from './plugin/load-data'
import { CustomTooltip } from './plugin/tooltip'
import { HideFreeNode } from './transform/hide-free-node'
import { treeDataToGraphNodeData } from './util'

register(ExtensionCategory.EDGE, 'custom-cubic-horizontal', CustomCubic)
register(ExtensionCategory.NODE, 'react', ReactNode)
register(ExtensionCategory.BEHAVIOR, 'hover-element', HoverElement)
register(ExtensionCategory.PLUGIN, 'remote-data-source', RemoteDataSource)
register(ExtensionCategory.PLUGIN, 'custom-tooltip', CustomTooltip)
register(ExtensionCategory.TRANSFORM, 'hide-free-node', HideFreeNode)
register(ExtensionCategory.BEHAVIOR, 'collapse-expand-tree', CollapseExpandTree)
register(ExtensionCategory.NODE, 'indented', IndentedNode)

export const LineageTreePage = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const graphRef = useRef<Graph>(null)

  const nodeStyle = useCallback((data: LineageNodeData) => {
    const style: NodeStyle = {
      component: <LineageNode data={data} />,
      ports: [{ placement: 'left' }, { placement: 'right' }],
      size: [160, 32],
      label: false,
    }
    return style
  }, [])

  useEffect(() => {
    const treeData = treeToGraphData(data, {
      getNodeData: treeDataToGraphNodeData,
    })

    const groupedNodesByCluster = treeData.nodes.reduce((acc, node) => {
      const tableType = node.data.tableType
      acc[tableType] ||= []
      acc[tableType].push(node.id)
      return acc
    }, {})

    const createStyle = baseColor => ({
      fill: baseColor,
      stroke: baseColor,
      labelFill: '#fff',
      labelPadding: 2,
      labelBackgroundFill: baseColor,
      labelBackgroundRadius: 5,
    })

    const graph = new Graph({
      container: containerRef.current as string | HTMLElement,
      autoFit: 'view',
      autoResize: true,
      zoomRange: [0.5, 2],
      // data: {
      //   ...treeData,
      //   combos: [
      //     { id: '1-A', data: { groupName: 'Group A' } },
      //     { id: '1-B', data: { groupName: 'Group B' } },
      //     { id: '1-C', data: { groupName: 'Group C' } },
      //     { id: '1-D', data: { groupName: 'Group D' } },
      //   ],
      //   // edges: [],
      // },
      transforms: [
        {
          type: 'hide-free-node',
        },
      ],
      plugins: [
        {
          type: 'minimap',
          size: [110, 50],
        },
        {
          type: 'toolbar',
          style: {
            backgroundColor: '#f5f5f5',
            padding: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            border: '1px solid #e8e8e8',
            opacity: '0.9',
            marginTop: '12px',
            marginLeft: '12px',
          },
          getItems: async () => {
            return Promise.resolve([
              {
                id: 'icon-huifushanchu',
                value: 'undelete',
              },
              {
                id: 'icon-shanchu4',
                value: 'delete',
              },
              {
                id: 'icon-quanbushanchu',
                value: 'all-delete',
              },
            ])
          },
          onClick: async value => {
            console.log('toolbar click', value)
            const nodes = ['1_1', '1_1_1']
            const edgeIds = ['0-1_1', '1_1-1_1_1']

            if (value === 'delete') {
              console.log('graph-node-before', graph.getNodeData())
              // await graph.hideElement([...nodes, ...edgeIds], true)
              // graph.frontElement([...nodes, ...edgeIds])
              // graph.removeData({
              //   nodes: nodes,
              //   // edges: edgeIds,
              // })
              graph.removeNodeData(data => data.map(d => d.id))
              // await graph.draw()

              console.log('graph-node-after', graph.getNodeData())
              graph.render()

              console.log('graph-node-after2', graph.getNodeData())
            }
            if (value === 'undelete') {
              graph.showElement(nodes, true).then(() => {
                // graph.render()
              })
              await graph.draw()
            }
            if (value === 'all-delete') {
              await graph.clear()
              await graph.draw()
            }
          },
        },
        {
          type: 'custom-tooltip',
          getContent: (_e, items) => {
            let result = `<h4>Custom Content</h4>`
            items.forEach(item => {
              result += `<p>Type: ${item.data.description}</p>`
            })
            return result
          },
        },
        // {
        //   key: 'hull-a',
        //   type: 'hull',
        //   members: groupedNodesByCluster['typeA'],
        //   labelText: 'cluster-a',
        //   ...createStyle('#1783FF'),
        // },
        {
          type: 'remote-data-source',
          maxLength: 500,
        },
        {
          type: 'collapse-expand-tree',
          // maxLength: 500,
        },
      ],
      node: {
        // palette: { field: 'tableType' },
        type: 'react',
        // style: {
        //   labelText: (d: NodeData) => d.name as string,
        //   labelPlacement: function (d) {
        //     const side = getNodeSide(this, d)
        //     return side === 'root' ? 'top' : side
        //   },
        //   ports: [{ placement: 'right' }, { placement: 'left' }],
        // },
        style: nodeStyle,
        state: {
          active: {
            halo: false,
          },
          selected: {
            halo: false,
          },
        },
        animation: {
          enter: false,
        },
      },
      edge: {
        type: 'custom-cubic-horizontal', // custom-cubic-horizontal
        style: {
          lineWidth: 1,
          radius: 20,
          stroke: '#8b9baf',
          endArrow: true,
          labelText: d => d.data?.text as string,
          labelFill: '#8b9baf',
          labelFontWeight: 600,
          labelBackground: true,
          labelBackgroundFill: '#f8f8f8',
          labelBackgroundOpacity: 1,
          labelBackgroundLineWidth: 1,
          labelBackgroundStroke: '#8b9baf',
          labelPadding: [1, 20],
          labelBackgroundRadius: 4,
          router: {
            type: 'orth',
          },
        },
        state: {
          active: {
            lineWidth: 2,
            stroke: '#000',
            labelBackgroundStroke: ACTIVE_COLOR,
            halo: false,
          },
        },
        animation: {
          enter: false,
        },
      },
      combo: {
        type: 'rect',
        style: d => {
          return {
            labelText: d.data?.groupName,
            labelFill: '#1A1A1A',
            labelFontSize: 12,
            labelPlacement: 'top-left',
            labelOffsetX: 47,
            labelOffsetY: 16,
            labelBackground: true, // 启用背景
            labelBackgroundFill: '#1783FF', // 蓝色背景
            labelBackgroundPadding: [4, 8], // 内边距：垂直4px，水平8px
            labelBackgroundRadius: 4, // 圆角半径
            fill: '#FFF1F0',
            fillOpacity: 0.1,
            // stroke: '#F5222D',
            lineWidth: 2,
            lineDash: [3, 4],
            lineCap: 'round',
          }
        },
      },
      layout: {
        type: 'mindmap',
        direction: 'H',
        preLayout: false,
        preventOverlap: true,
        getHeight: () => 50,
        getWidth: () => 120,
        // biome-ignore lint/style/useNamingConvention: false positive
        getVGap: () => 4,
        // biome-ignore lint/style/useNamingConvention: false positive
        getHGap: () => 64,
        getSide: (d: NodeData) => {
          const nodeData = graph.getNodeData(d.id)
          return nodeData.position
        },
      },
      behaviors: ['collapse-expand', 'drag-canvas', 'zoom-canvas', 'hover-element', 'click-select'],
    })

    graphRef.current = graph
    graphRef.current.render()

    return () => {
      graphRef.current?.destroy?.()
    }
  }, [nodeStyle])

  useEffect(() => {
    // 监听窗口的变更
    const handleResize = () => {
      graphRef.current?.fitView?.()
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return <div style={{ width: '100%', height: '100%' }} ref={containerRef}></div>
}
